import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { ArrowLeft, MapPin, Building, Clock, PoundSterling, ExternalLink, Calendar, User } from 'lucide-react';
import axios from 'axios';
import { useParams } from 'react-router-dom';
import { useStructuredData } from '../hooks/useStructuredData';
import { generateJobPostingSchema } from '../utils/structuredData';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const JobPage = ({ onBackToHome }) => {
  const { jobId } = useParams();
  const [job, setJob] = useState(null);
  const [loading, setLoading] = useState(true);

  // Generate structured data for the job
  const jobSchema = React.useMemo(() => {
    return job ? generateJobPostingSchema(job) : null;
  }, [job]);

  // Apply structured data
  useStructuredData(jobSchema, 'job-schema');

  useEffect(() => {
    const fetchJob = async () => {
      try {
        const response = await axios.get(`${API}/jobs/${jobId}`);
        setJob(response.data);
      } catch (error) {
        console.error('Error fetching job:', error);
        setJob(null);
      } finally {
        setLoading(false);
      }
    };

    if (jobId) {
      fetchJob();
    }
  }, [jobId]);

  const getJobTypeBadgeColor = (type) => {
    const colors = {
      'full-time': 'bg-green-100 text-green-800',
      'part-time': 'bg-blue-100 text-blue-800',
      'contract': 'bg-orange-100 text-orange-800',
      'temporary': 'bg-yellow-100 text-yellow-800'
    };
    return colors[type?.toLowerCase()] || colors['full-time'];
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-GB', {
      day: 'numeric',
      month: 'long',
      year: 'numeric'
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
          <p className="text-gray-600">Loading job details...</p>
        </div>
      </div>
    );
  }

  if (!job) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-charcoal mb-4">Job Not Found</h1>
          <p className="text-gray-600 mb-6">The job posting you're looking for doesn't exist or has expired.</p>
          <Button onClick={onBackToHome}>
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to Jobs
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="container mx-auto px-4 py-8">
        <div className="flex items-center justify-between mb-8">
          <Button 
            onClick={onBackToHome}
            variant="outline"
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to Jobs
          </Button>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Main Job Info */}
          <div className="lg:col-span-2">
            <Card className="mb-6">
              <CardHeader>
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <CardTitle className="text-3xl font-bold text-charcoal mb-2">
                      {job.title}
                    </CardTitle>
                    <div className="flex items-center gap-2 mb-4">
                      <Badge className={getJobTypeBadgeColor(job.job_type || job.type)}>
                        {(job.job_type || job.type)?.replace('-', ' ').toUpperCase() || 'FULL-TIME'}
                      </Badge>
                      {job.is_featured && (
                        <Badge className="bg-amber-100 text-amber-800">
                          ⭐ Featured
                        </Badge>
                      )}
                    </div>
                    <div className="flex items-center gap-4 text-gray-600">
                      <div className="flex items-center gap-1">
                        <Building className="h-4 w-4" />
                        <span>{job.company}</span>
                      </div>
                      <div className="flex items-center gap-1">
                        <MapPin className="h-4 w-4" />
                        <span>{job.location}</span>
                      </div>
                      {job.salary_range && (
                        <div className="flex items-center gap-1">
                          <PoundSterling className="h-4 w-4" />
                          <span>{job.salary_range}</span>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                <div className="mb-6">
                  <h3 className="text-xl font-semibold text-charcoal mb-3">
                    Job Description
                  </h3>
                  <div className="prose max-w-none">
                    <p className="text-gray-700 leading-relaxed whitespace-pre-wrap">
                      {job.description}
                    </p>
                  </div>
                </div>

                {/* Requirements */}
                {job.requirements && job.requirements.length > 0 && (
                  <div className="mb-6">
                    <h3 className="text-xl font-semibold text-charcoal mb-3">
                      Requirements
                    </h3>
                    <ul className="space-y-2">
                      {job.requirements.map((requirement, index) => (
                        <li key={index} className="flex items-start gap-2">
                          <span className="text-primary mt-2">•</span>
                          <span className="text-gray-700">{requirement}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                {/* Benefits */}
                {job.benefits && job.benefits.length > 0 && (
                  <div className="mb-6">
                    <h3 className="text-xl font-semibold text-charcoal mb-3">
                      Benefits
                    </h3>
                    <ul className="space-y-2">
                      {job.benefits.map((benefit, index) => (
                        <li key={index} className="flex items-start gap-2">
                          <span className="text-green-600 mt-2">✓</span>
                          <span className="text-gray-700">{benefit}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                {/* Apply Section */}
                <div className="bg-primary/5 p-6 rounded-lg">
                  <h3 className="text-xl font-semibold text-charcoal mb-3">
                    Ready to Apply?
                  </h3>
                  <p className="text-gray-700 mb-4">
                    Click the button below to apply for this position directly with {job.company}.
                  </p>
                  <Button 
                    className="w-full sm:w-auto"
                    onClick={() => window.open(job.application_url, '_blank')}
                  >
                    <ExternalLink className="h-4 w-4 mr-2" />
                    Apply Now
                  </Button>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Job Details Sidebar */}
          <div className="lg:col-span-1">
            <Card className="mb-6">
              <CardHeader>
                <CardTitle>Job Details</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <h4 className="font-medium text-charcoal mb-1">Company</h4>
                  <p className="text-gray-600">{job.company}</p>
                </div>

                <div>
                  <h4 className="font-medium text-charcoal mb-1">Location</h4>
                  <p className="text-gray-600">{job.location}</p>
                </div>

                <div>
                  <h4 className="font-medium text-charcoal mb-1">Employment Type</h4>
                  <Badge className={getJobTypeBadgeColor(job.job_type || job.type)}>
                    {(job.job_type || job.type)?.replace('-', ' ').toUpperCase() || 'FULL-TIME'}
                  </Badge>
                </div>

                {job.salary_range && (
                  <div>
                    <h4 className="font-medium text-charcoal mb-1">Salary</h4>
                    <p className="text-gray-600">{job.salary_range}</p>
                  </div>
                )}

                <div>
                  <h4 className="font-medium text-charcoal mb-1">Posted</h4>
                  <p className="text-gray-600">{formatDate(job.created_at)}</p>
                </div>

                {job.expires_at && (
                  <div>
                    <h4 className="font-medium text-charcoal mb-1">Expires</h4>
                    <p className="text-red-600">{formatDate(job.expires_at)}</p>
                  </div>
                )}

                {job.contact_email && (
                  <div>
                    <h4 className="font-medium text-charcoal mb-1">Contact</h4>
                    <a 
                      href={`mailto:${job.contact_email}`}
                      className="text-primary hover:text-orange"
                    >
                      {job.contact_email}
                    </a>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Apply Button */}
            <Card>
              <CardContent className="p-6">
                <Button 
                  className="w-full mb-3"
                  onClick={() => window.open(job.application_url, '_blank')}
                >
                  <ExternalLink className="h-4 w-4 mr-2" />
                  Apply for This Job
                </Button>
                <p className="text-xs text-gray-500 text-center">
                  You'll be redirected to the company's application page
                </p>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
};

export default JobPage;